import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Trash2 } from "react-feather";
import Select, { components } from "react-select";
import BaseButton from "../../../../BaseComponents/BaseButton";
import BaseTooltip from "../../../../BaseComponents/BaseTooltip";
import { listPtSlabsApi, savePtSlabsApi } from "../../../../Api/payrollApi";
import { INDIAN_STATE_OPTIONS, PAYROLL_STRINGS } from "../../../../Constant/HR/payrollConstants";
import { checkStatusCodeSuccess, rendorBackButton } from "../../../../Constant/common";
import "./TaxSlabSettings.css";

const CustomInput = (props) => <components.Input {...props} data-rs-input="true" />;

const buildStyles = (isInvalid) => ({
  control: (_, state) => ({
    display: "flex",
    alignItems: "center",
    flexWrap: "nowrap",
    backgroundColor: "var(--input-bg)",
    border: `1px solid ${
      isInvalid
        ? "var(--danger)"
        : state.isFocused
          ? "var(--input-border-focus)"
          : "var(--input-border)"
    }`,
    borderRadius: 12,
    minHeight: 40,
    height: 40,
    boxShadow: state.isFocused
      ? isInvalid
        ? "0 0 0 3px rgba(244,63,94,0.1)"
        : "0 0 0 3px rgba(99,102,241,0.12)"
      : "none",
    cursor: state.isDisabled ? "not-allowed" : "pointer",
    pointerEvents: "auto",
    transition: "border-color 0.2s, box-shadow 0.2s",
  }),
  valueContainer: () => ({
    display: "flex",
    alignItems: "center",
    flex: 1,
    flexWrap: "wrap",
    padding: "0 10px",
    backgroundColor: "transparent",
    overflow: "hidden",
    height: "100%",
    position: "relative",
  }),
  input: () => ({
    backgroundColor: "transparent",
    color: "var(--text-primary)",
    margin: 0,
    padding: 0,
    fontSize: "0.8125rem",
    fontFamily: "Inter, sans-serif",
    gridArea: "1 / 2",
    minWidth: 2,
    border: 0,
    outline: 0,
    position: "relative",
    zIndex: 1,
  }),
  singleValue: () => ({
    color: "var(--text-primary)",
    fontSize: "0.8125rem",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    maxWidth: "calc(100% - 8px)",
  }),
  placeholder: () => ({
    color: "var(--text-muted)",
    fontSize: "0.8125rem",
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    pointerEvents: "none",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "calc(100% - 8px)",
  }),
  indicatorsContainer: () => ({
    display: "flex",
    alignItems: "center",
    backgroundColor: "transparent",
    flexShrink: 0,
  }),
  indicatorSeparator: () => ({
    width: 1,
    backgroundColor: "var(--input-border)",
    alignSelf: "stretch",
    margin: "8px 0",
  }),
  dropdownIndicator: (_, state) => ({
    display: "flex",
    alignItems: "center",
    padding: "0 8px",
    color: "var(--text-muted)",
    transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : "rotate(0deg)",
    transition: "transform 0.2s",
  }),
  clearIndicator: () => ({
    display: "flex",
    alignItems: "center",
    padding: "0 4px",
    color: "var(--text-muted)",
    cursor: "pointer",
  }),
  menu: () => ({
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    marginTop: 4,
    backgroundColor: "var(--dropdown-bg)",
    border: "1px solid var(--dropdown-border)",
    borderRadius: 12,
    boxShadow: "0 16px 48px rgba(0,0,0,0.3)",
    zIndex: 9999,
    overflow: "hidden",
  }),
  menuList: () => ({
    padding: 4,
    maxHeight: 260,
    overflowY: "auto",
  }),
  option: (_, state) => ({
    backgroundColor: state.isSelected
      ? "rgba(99,102,241,0.15)"
      : state.isFocused
        ? "var(--select-option-hover)"
        : "transparent",
    color: state.isSelected ? "var(--brand-indigo)" : "var(--text-primary)",
    fontSize: "0.8125rem",
    fontWeight: state.isSelected ? 600 : 400,
    padding: "9px 12px",
    borderRadius: 8,
    cursor: "pointer",
  }),
  noOptionsMessage: () => ({
    color: "var(--text-muted)",
    fontSize: "0.8125rem",
    padding: "10px 12px",
    textAlign: "center",
  }),
  groupHeading: () => ({
    fontSize: "0.625rem",
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: "var(--brand-indigo)",
    padding: "8px 12px 4px",
    background: "rgba(99,102,241,0.04)",
    borderRadius: 6,
    margin: "4px 0 2px",
  }),
  multiValue: () => ({
    display: "flex",
    alignItems: "center",
    backgroundColor: "rgba(99,102,241,0.12)",
    borderRadius: 6,
    margin: 2,
  }),
  multiValueLabel: () => ({
    color: "var(--brand-indigo)",
    fontSize: "0.75rem",
    padding: "2px 6px",
  }),
  multiValueRemove: () => ({
    display: "flex",
    alignItems: "center",
    padding: "0 4px",
    color: "var(--brand-indigo)",
    cursor: "pointer",
    borderRadius: "0 6px 6px 0",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
});

let tempIdCounter = 1;
const genTempId = () => `temp_${tempIdCounter++}`;

const PtSlabConfig = () => {
  const navigate = useNavigate();
  const [slabs, setSlabs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Declare fetchSlabs before useEffect so it is not accessed before declaration
  const fetchSlabs = useCallback(() => {
    setIsLoading(true);
    listPtSlabsApi()
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          const data = resp?.data ?? [];
          setSlabs(
            data.map((s) => ({
              ...s,
              _id: s.id ?? genTempId(),
            })),
          );
        }
      })
      .catch((err) => err)
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    fetchSlabs(); // eslint-disable-line react-hooks/set-state-in-effect
  }, [fetchSlabs]);

  const handleAddRow = () => {
    setSlabs((prev) => [
      ...prev,
      {
        _id: genTempId(),
        state: "",
        salary_from: "",
        salary_to: "",
        pt_amount: "",
      },
    ]);
  };

  const handleChange = (id, field, value) => {
    setSlabs((prev) => prev.map((s) => (s._id === id ? { ...s, [field]: value } : s)));
  };

  const handleDelete = (id) => {
    setSlabs((prev) => prev.filter((s) => s._id !== id));
  };

  const handleSave = () => {
    // eslint-disable-next-line no-unused-vars
    const payload = slabs.map(({ _id, ...rest }) => ({
      ...rest,
      salary_from: rest.salary_from === "" ? null : Number(rest.salary_from),
      salary_to: rest.salary_to === "" ? null : Number(rest.salary_to),
      pt_amount: rest.pt_amount === "" ? null : Number(rest.pt_amount),
    }));

    setIsSaving(true);
    savePtSlabsApi(payload)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          toast.success(PAYROLL_STRINGS.SUCCESS_SLABS_SAVED);
          fetchSlabs();
        } else {
          toast.error(PAYROLL_STRINGS.ERROR_GENERIC);
        }
      })
      .catch(() => toast.error(PAYROLL_STRINGS.ERROR_GENERIC))
      .finally(() => setIsSaving(false));
  };

  return (
    <div className="animate-fade-in d-flex flex-column gap-3">
      {/* Page header */}
      <div className="animate-slide-down d-flex align-items-center justify-content-between flex-wrap gap-3">
        <div>
          <h1 className="page-header-title">{PAYROLL_STRINGS.SETTINGS_PT_TITLE}</h1>
          <p className="page-header-subtitle">{PAYROLL_STRINGS.SETTINGS_PT_SUBTITLE}</p>
        </div>
        {rendorBackButton(() => navigate("/payrollTemplates"))}
      </div>

      {/* Content card */}
      <div className="glass rounded-3 p-5 animate-slide-up">
        {/* Toolbar */}
        <div className="d-flex justify-content-end gap-2 mb-4">
          <BaseButton
            label={PAYROLL_STRINGS.BTN_ADD_ROW}
            color="secondary"
            onClick={handleAddRow}
            disabled={isLoading}
          />
          <BaseButton
            label={isSaving ? PAYROLL_STRINGS.LOADING_SAVING : PAYROLL_STRINGS.BTN_SAVE_SLABS}
            color="primary"
            loader={isSaving}
            onClick={handleSave}
            disabled={isLoading || isSaving}
          />
        </div>

        {/* Table */}
        <div style={{ overflowX: "auto" }}>
          <table className="table table-sm table-hover" style={{ width: "100%" }}>
            <thead className="border-bottom" style={{ background: "var(--glass-bg)" }}>
              <tr>
                {["State", "Salary From (₹)", "Salary To (₹)", "PT Amount (₹)", ""].map((col) => (
                  <th
                    key={col}
                    className="p-3 fs-8 fw-bold text-uppercase text-start"
                    style={{
                      color: "var(--text-primary)",
                      letterSpacing: "0.08em",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    colSpan={5}
                    className="p-5 text-center"
                    style={{ color: "var(--text-primary)" }}
                  >
                    Loading…
                  </td>
                </tr>
              ) : slabs.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="p-5 text-center"
                    style={{ color: "var(--text-primary)" }}
                  >
                    No slabs configured. Click &quot;Add Row&quot; to begin.
                  </td>
                </tr>
              ) : (
                slabs.map((slab) => (
                  <tr key={slab._id} className="border-bottom">
                    <td className="p-3" style={{ minWidth: 180, verticalAlign: "middle" }}>
                      <Select
                        inputId={`state_${slab._id}`}
                        name="state"
                        unstyled
                        styles={buildStyles(false)}
                        components={{ Input: CustomInput }}
                        options={INDIAN_STATE_OPTIONS}
                        placeholder="Select state"
                        value={INDIAN_STATE_OPTIONS.find((o) => o.value === slab.state) || null}
                        onChange={(selected) =>
                          handleChange(slab._id, "state", selected ? selected.value : "")
                        }
                        menuPortalTarget={document.body}
                      />
                    </td>

                    {/* Salary From */}
                    <td className="p-3" style={{ verticalAlign: "middle" }}>
                      <input
                        type="number"
                        aria-label="Salary From"
                        value={slab.salary_from ?? ""}
                        min={0}
                        onChange={(e) => handleChange(slab._id, "salary_from", e.target.value)}
                        className="form-control form-control-sm"
                        style={{
                          minWidth: 120,
                          borderRadius: 12,
                          border: "1px solid var(--glass-border)",
                          background: "var(--glass-bg)",
                          color: "var(--text-primary)",
                          fontSize: "0.875rem",
                        }}
                      />
                    </td>

                    {/* Salary To */}
                    <td className="p-3" style={{ verticalAlign: "middle" }}>
                      <input
                        type="number"
                        aria-label="Salary To"
                        placeholder="No limit"
                        value={slab.salary_to ?? ""}
                        min={0}
                        onChange={(e) => handleChange(slab._id, "salary_to", e.target.value)}
                        className="form-control form-control-sm"
                        style={{
                          minWidth: 120,
                          borderRadius: 12,
                          border: "1px solid var(--glass-border)",
                          background: "var(--glass-bg)",
                          color: "var(--text-primary)",
                          fontSize: "0.875rem",
                        }}
                      />
                    </td>

                    {/* PT Amount */}
                    <td className="p-3" style={{ verticalAlign: "middle" }}>
                      <input
                        type="number"
                        aria-label="PT Amount"
                        value={slab.pt_amount ?? ""}
                        min={0}
                        onChange={(e) => handleChange(slab._id, "pt_amount", e.target.value)}
                        className="form-control form-control-sm"
                        style={{
                          minWidth: 120,
                          borderRadius: 12,
                          border: "1px solid var(--glass-border)",
                          background: "var(--glass-bg)",
                          color: "var(--text-primary)",
                          fontSize: "0.875rem",
                        }}
                      />
                    </td>

                    {/* Delete */}
                    <td className="p-3 text-center" style={{ verticalAlign: "middle" }}>
                      <BaseTooltip label="Delete This PT Slab" color="#be123c">
                        <BaseButton
                          icon={<Trash2 size={14} strokeWidth={2} />}
                          onClick={() => handleDelete(slab._id)}
                          aria-label="Delete row"
                          color="light"
                          className="slab-delete-btn"
                        />
                      </BaseTooltip>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PtSlabConfig;
